import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import Block from "./Block";
import url from "../serverurl";
import "./pecuscan-terminal.css";
import { Filter, TuneOutlined } from "@mui/icons-material";

const getBlockNumber = (id) => parseInt(id) + 6000000;

const blockValue = (trans) =>
  trans && trans.length > 0
    ? trans.reduce((a, b) => Number(a) + Number(b.price), 0)
    : 0;

const fmt = (n, decimals = 0) =>
  n === null || n === undefined || isNaN(n)
    ? "—"
    : Number(n).toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

/* ============================================================
   ADVANCED FILTER CONFIG
   Each field defines: how it renders, what operators it allows,
   and how it evaluates against a block/tx record.
   ============================================================ */
const FIELD_DEFS = {
  block_number: {
    label: "Block Number",
    type: "number",
    scope: "block",
    ops: ["=", "!=", ">", "<", ">=", "<=", "between"],
    get: (b) => getBlockNumber(b.index),
  },
  block_value_usd: {
    label: "Block Value (USD)",
    type: "number",
    scope: "block",
    ops: ["=", "!=", ">", "<", ">=", "<=", "between"],
    get: (b) => blockValue(b.data),
  },
  tx_count: {
    label: "Tx Count in Block",
    type: "number",
    scope: "block",
    ops: ["=", "!=", ">", "<", ">=", "<=", "between"],
    get: (b) => (b.data ? b.data.length : 0),
  },
  block_hash: {
    label: "Block Hash",
    type: "string",
    scope: "block",
    ops: ["=", "!=", "contains", "starts_with"],
    get: (b) => b.hash || "",
  },
  timestamp: {
    label: "Timestamp",
    type: "date",
    scope: "block",
    ops: [">", "<", ">=", "<=", "between"],
    get: (b) => (b.timestamp ? new Date(b.timestamp).getTime() : 0),
  },
  tx_hash: {
    label: "Tx Hash",
    type: "string",
    scope: "tx",
    ops: ["=", "!=", "contains", "starts_with"],
    get: (t) => t?.hash || t?.txid || "",
  },
  tx_from: {
    label: "From Address",
    type: "string",
    scope: "tx",
    ops: ["=", "!=", "contains", "starts_with"],
    get: (t) => t?.from || t?.sender || "",
  },
  tx_to: {
    label: "To Address",
    type: "string",
    scope: "tx",
    ops: ["=", "!=", "contains", "starts_with"],
    get: (t) => t?.to || t?.receiver || "",
  },
  tx_value: {
    label: "Tx Value (PECU)",
    type: "number",
    scope: "tx",
    ops: ["=", "!=", ">", "<", ">=", "<=", "between"],
    get: (t) => Number(t?.price ?? t?.value ?? 0),
  },
  token: {
    label: "Token Symbol",
    type: "string",
    scope: "tx",
    ops: ["=", "!=", "contains"],
    get: (t) => t?.token || t?.symbol || "PECU",
  },
  label_tag: {
    label: "Label / Tag",
    type: "string",
    scope: "tx",
    ops: ["=", "!=", "contains"],
    get: (t) => t?.label || t?.tag || "",
  },
};

const OP_LABELS = {
  "=": "=",
  "!=": "≠",
  ">": ">",
  "<": "<",
  ">=": "≥",
  "<=": "≤",
  between: "between",
  contains: "contains",
  starts_with: "starts with",
};

const newRule = () => ({
  id: Math.random().toString(36).slice(2, 9),
  field: "block_number",
  op: "=",
  value: "",
  value2: "",
});

const newGroup = (combinator = "AND") => ({
  id: Math.random().toString(36).slice(2, 9),
  type: "group",
  combinator, // AND | OR
  not: false,
  children: [newRule()],
});

/* Evaluate a single rule against a record */
const evalRule = (rule, record) => {
  const def = FIELD_DEFS[rule.field];
  if (!def) return true;
  const left = def.get(record);
  const right = rule.value;
  const right2 = rule.value2;

  if (def.type === "number" || def.type === "date") {
    const L = Number(left);
    const R = Number(right);
    const R2 = Number(right2);
    switch (rule.op) {
      case "=": return L === R;
      case "!=": return L !== R;
      case ">": return L > R;
      case "<": return L < R;
      case ">=": return L >= R;
      case "<=": return L <= R;
      case "between": return L >= Math.min(R, R2) && L <= Math.max(R, R2);
      default: return true;
    }
  }

  // string
  const L = String(left ?? "").toLowerCase();
  const R = String(right ?? "").toLowerCase();
  switch (rule.op) {
    case "=": return L === R;
    case "!=": return L !== R;
    case "contains": return L.includes(R);
    case "starts_with": return L.startsWith(R);
    default: return true;
  }
};

/* Evaluate a group/rule tree against a single block */
const evalNode = (node, block) => {
  if (!node) return true;
  if (node.type === "group") {
    const results = node.children.map((c) => evalNode(c, block));
    let combined =
      node.combinator === "OR"
        ? results.some(Boolean)
        : results.every(Boolean);
    if (node.children.length === 0) combined = true;
    return node.not ? !combined : combined;
  }
  // it's a rule
  const def = FIELD_DEFS[node.field];
  if (!def) return true;
  if (def.scope === "block") return evalRule(node, block);
  // tx-scope: block matches if ANY tx in it matches
  const txs = block.data || [];
  if (txs.length === 0) return false;
  return txs.some((t) => evalRule(node, t));
};

function SearchBar() {
  const navigate = useNavigate();
  const [searchBlock, setSearchBlock] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchNotice, setSearchNotice] = useState("");
  const [clock, setClock] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterScope, setFilterScope] = useState("ALL");
  const [queryTree, setQueryTree] = useState(() => newGroup("AND"));
  const [filterActive, setFilterActive] = useState(false);

  // Blocks
  const [blocks, setBlocks] = useState([]);
  const [blocksLoading, setBlocksLoading] = useState(true);

  // Market data
  const [currentValue, setCurrentValue] = useState(null);
  const [marketCap, setMarketCap] = useState(null);
  const [circulating, setCirculating] = useState(null);
  const [totalSupply, setTotalSupply] = useState(null);
  const [maxSupply, setMaxSupply] = useState(null);
  const [coinVolume, setCoinVolume] = useState(null);
  const [trendHourly, setTrendHourly] = useState(null);
  const [trend24, setTrend24] = useState(null);

  const valChartRef = useRef(null);
  const volChartRef = useRef(null);
  const valChartInst = useRef(null);
  const volChartInst = useRef(null);

  // ===== Live clock =====
  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setClock(d.toISOString().slice(11, 19) + " UTC");
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  // ===== Fetch blocks =====
  useEffect(() => {
    const fetchBlocks = () => {
      axios
        .get(`${url}/blockchain/blocks?page=1`)
        .then((res) => {
          setBlocks(res.data.blocks || []);
          setBlocksLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setBlocksLoading(false);
        });
    };
    fetchBlocks();
    const interval = setInterval(fetchBlocks, 10000);
    return () => clearInterval(interval);
  }, []);

  // ===== Fetch market data =====
  useEffect(() => {
    const fetchAll = () => {
      axios.get(`${url}/wallet/get_current_index_coin`)
        .then((res) => setCurrentValue(res.data?.[0]?.value ?? null)).catch(() => {});
      axios.get(`${url}/wallet/marketcap`)
        .then((res) => setMarketCap(res.data?.MARKET_CAP ?? null)).catch(() => {});
      axios.get(`${url}/wallet/market`)
        .then((res) => {
          setCirculating(res.data?.circulating_supply ?? null);
          setTotalSupply(res.data?.total_supply ?? null);
          setMaxSupply(res.data?.max_supply ?? null);
        }).catch(() => {});
      axios.get(`${url}/wallet/calculate_daily_volume`)
        .then((res) => setCoinVolume(res.data?.total_volume ?? null)).catch(() => {});
      axios.get(`${url}/wallet/get_change_index_coin_hourly`)
        .then((res) => setTrendHourly(res.data ?? null)).catch(() => {});
      axios.get(`${url}/wallet/get_change_index_coin`)
        .then((res) => setTrend24(res.data ?? null)).catch(() => {});
    };
    fetchAll();
    const interval = setInterval(fetchAll, 10000);
    return () => clearInterval(interval);
  }, []);

  // Derived
  const latestBlock = blocks[0] ? getBlockNumber(blocks[0].index) : null;
  const usdVolume =
    coinVolume !== null && currentValue !== null ? coinVolume * currentValue : null;

  /* ============================================================
     UNIFIED SEARCH — hits /blockchain/search and routes to the
     matching entity's detail page (block / tx / escrow). "address"
     has no dedicated page yet, so it surfaces an inline notice
     instead of a broken link.
     ============================================================ */
  const runSearch = async () => {
    const q = searchBlock.trim();
    if (!q || searching) return;

    setSearching(true);
    setSearchNotice("");
    try {
      const res = await axios.get(`${url}/blockchain/search`, { params: { q } });
      const { kind, value } = res.data || {};

      if (kind === "block" && value) {
        navigate(`/block/${value.hash}`, { state: value });
      } else if (kind === "transaction" && value?.hash) {
        navigate(`/tx/${value.hash}`);
      } else if (kind === "escrow" && value?.pubKey) {
        navigate(`/escrow/${value.pubKey}`);
      } else if (kind === "address") {
        setSearchNotice(`Address "${q}" has activity, but there's no address page yet.`);
      } else {
        setSearchNotice(`No block, transaction, escrow, or address matched "${q}".`);
      }
    } catch (err) {
      console.log(err);
      setSearchNotice("Search failed — please try again.");
    } finally {
      setSearching(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") runSearch();
  };

  /* ============================================================
     APPLY FILTERS — produce a filtered view of blocks
     ============================================================ */
  const filteredBlocks = useMemo(() => {
    if (!filterActive) return blocks;
    return blocks.filter((b) => evalNode(queryTree, b));
  }, [blocks, queryTree, filterActive]);

  const ruleCount = useMemo(() => {
    const count = (n) =>
      n.type === "group" ? n.children.reduce((a, c) => a + count(c), 0) : 1;
    return count(queryTree);
  }, [queryTree]);

  /* ============================================================
     QUERY TREE EDITORS (immutable updates)
     ============================================================ */
  const updateNode = (rootNode, id, updater) => {
    if (rootNode.id === id) return updater(rootNode);
    if (rootNode.type === "group") {
      return {
        ...rootNode,
        children: rootNode.children.map((c) => updateNode(c, id, updater)),
      };
    }
    return rootNode;
  };

  const removeNode = (rootNode, id) => {
    if (rootNode.type !== "group") return rootNode;
    return {
      ...rootNode,
      children: rootNode.children
        .filter((c) => c.id !== id)
        .map((c) => removeNode(c, id)),
    };
  };

  const addRuleToGroup = (groupId) =>
    setQueryTree((t) =>
      updateNode(t, groupId, (g) => ({ ...g, children: [...g.children, newRule()] }))
    );

  const addGroupToGroup = (groupId) =>
    setQueryTree((t) =>
      updateNode(t, groupId, (g) => ({ ...g, children: [...g.children, newGroup("AND")] }))
    );

  const handleApply = () => {
    setFilterActive(true);
    setFilterOpen(false);
  };

  const handleClear = () => {
    setQueryTree(newGroup("AND"));
    setFilterActive(false);
  };

  /* ============================================================
     CHARTS — use FILTERED blocks
     ============================================================ */
  useEffect(() => {
    if (filteredBlocks.length === 0) return;

    const loadChart = () => {
      if (window.Chart) { renderCharts(); return; }
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
      script.onload = renderCharts;
      document.head.appendChild(script);
    };

    const renderCharts = () => {
      const Chart = window.Chart;
      if (!Chart) return;

      const recent = [...filteredBlocks].slice(0, 20).reverse();
      const labels = recent.map((b) => getBlockNumber(b.index));
      const txCounts = recent.map((b) => b.data?.length || 0);
      const values = recent.map((b) => blockValue(b.data));

      const baseOpts = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#111827", borderColor: "#1f2937", borderWidth: 1,
            titleColor: "#f59e0b", bodyColor: "#e5e7eb",
            titleFont: { family: "JetBrains Mono", size: 10 },
            bodyFont: { family: "JetBrains Mono", size: 11 },
          },
        },
        scales: {
          x: {
            grid: { color: "#1a2332", drawBorder: false },
            ticks: { color: "#6b7280", font: { family: "JetBrains Mono", size: 9 }, maxTicksLimit: 8 },
          },
          y: {
            grid: { color: "#1a2332", drawBorder: false },
            ticks: { color: "#6b7280", font: { family: "JetBrains Mono", size: 9 } },
          },
        },
      };

      if (valChartRef.current) {
        if (valChartInst.current) valChartInst.current.destroy();
        valChartInst.current = new Chart(valChartRef.current, {
          type: "line",
          data: {
            labels,
            datasets: [{
              label: "Block value (USD)",
              data: values,
              borderColor: "#10b981",
              backgroundColor: "rgba(16,185,129,0.08)",
              borderWidth: 1.5, fill: true, tension: 0.3,
              pointRadius: 0, pointHoverRadius: 4,
              pointHoverBackgroundColor: "#f59e0b",
            }],
          },
          options: {
            ...baseOpts,
            scales: {
              ...baseOpts.scales,
              y: {
                ...baseOpts.scales.y,
                ticks: {
                  ...baseOpts.scales.y.ticks,
                  callback: (v) => "$" + Number(v).toFixed(2),
                },
              },
            },
          },
        });
      }

      if (volChartRef.current) {
        if (volChartInst.current) volChartInst.current.destroy();
        volChartInst.current = new Chart(volChartRef.current, {
          type: "bar",
          data: {
            labels,
            datasets: [{
              label: "Transactions", data: txCounts,
              backgroundColor: "#f59e0b", borderRadius: 0, barThickness: 10,
            }],
          },
          options: baseOpts,
        });
      }
    };

    loadChart();
    return () => {
      if (volChartInst.current) volChartInst.current.destroy();
      if (valChartInst.current) valChartInst.current.destroy();
    };
  }, [filteredBlocks]);

  const renderTrend = (t) => {
    if (!t) return <span className="tick-val">—</span>;
    const up = t.trend === 1;
    return (
      <span className={up ? "tick-up" : "tick-dn"}>
        {up ? "▲" : "▼"} {t.value}%
      </span>
    );
  };

  /* ============================================================
     RULE / GROUP RENDERERS
     ============================================================ */
  const renderRule = (rule) => {
    const def = FIELD_DEFS[rule.field];
    const allowedFields = Object.entries(FIELD_DEFS).filter(
      ([, d]) =>
        filterScope === "ALL" ||
        (filterScope === "BLOCK" && d.scope === "block") ||
        (filterScope === "TX" && d.scope === "tx")
    );

    return (
      <div key={rule.id} className="qb-rule">
        <select
          className="qb-select"
          value={rule.field}
          onChange={(e) =>
            setQueryTree((t) =>
              updateNode(t, rule.id, (r) => {
                const nd = FIELD_DEFS[e.target.value];
                return { ...r, field: e.target.value, op: nd.ops[0], value: "", value2: "" };
              })
            )
          }
        >
          {allowedFields.map(([k, d]) => (
            <option key={k} value={k}>
              {d.label} {d.scope === "tx" ? "·tx" : "·blk"}
            </option>
          ))}
        </select>

        <select
          className="qb-select qb-op"
          value={rule.op}
          onChange={(e) =>
            setQueryTree((t) =>
              updateNode(t, rule.id, (r) => ({ ...r, op: e.target.value }))
            )
          }
        >
          {def.ops.map((op) => (
            <option key={op} value={op}>{OP_LABELS[op]}</option>
          ))}
        </select>

        <input
          className="qb-input"
          type={def.type === "number" ? "number" : def.type === "date" ? "datetime-local" : "text"}
          placeholder={def.type === "string" ? "value..." : ""}
          value={rule.value}
          onChange={(e) =>
            setQueryTree((t) =>
              updateNode(t, rule.id, (r) => ({ ...r, value: e.target.value }))
            )
          }
        />

        {rule.op === "between" && (
          <>
            <span className="qb-and">AND</span>
            <input
              className="qb-input"
              type={def.type === "number" ? "number" : def.type === "date" ? "datetime-local" : "text"}
              value={rule.value2}
              onChange={(e) =>
                setQueryTree((t) =>
                  updateNode(t, rule.id, (r) => ({ ...r, value2: e.target.value }))
                )
              }
            />
          </>
        )}

        <button
          type="button"
          className="qb-btn qb-btn-danger"
          onClick={() => setQueryTree((t) => removeNode(t, rule.id))}
          title="Remove"
        >
          ✕
        </button>
      </div>
    );
  };

  const renderGroup = (group, depth = 0) => (
    <div key={group.id} className={`qb-group depth-${Math.min(depth, 3)}`}>
      <div className="qb-group-head">
        <div className="qb-combinator">
          <button
            type="button"
            className={`qb-pill ${group.combinator === "AND" ? "active" : ""}`}
            onClick={() =>
              setQueryTree((t) =>
                updateNode(t, group.id, (g) => ({ ...g, combinator: "AND" }))
              )
            }
          >
            AND
          </button>
          <button
            type="button"
            className={`qb-pill ${group.combinator === "OR" ? "active" : ""}`}
            onClick={() =>
              setQueryTree((t) =>
                updateNode(t, group.id, (g) => ({ ...g, combinator: "OR" }))
              )
            }
          >
            OR
          </button>
          <button
            type="button"
            className={`qb-pill qb-pill-not ${group.not ? "active" : ""}`}
            onClick={() =>
              setQueryTree((t) =>
                updateNode(t, group.id, (g) => ({ ...g, not: !g.not }))
              )
            }
            title="Negate this group"
          >
            NOT
          </button>
        </div>

        <div className="qb-group-actions">
          <button type="button" className="qb-btn" onClick={() => addRuleToGroup(group.id)}>
            + RULE
          </button>
          <button type="button" className="qb-btn" onClick={() => addGroupToGroup(group.id)}>
            + GROUP
          </button>
          {depth > 0 && (
            <button
              type="button"
              className="qb-btn qb-btn-danger"
              onClick={() => setQueryTree((t) => removeNode(t, group.id))}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="qb-group-body">
        {group.children.map((child) =>
          child.type === "group" ? renderGroup(child, depth + 1) : renderRule(child)
        )}
        {group.children.length === 0 && (
          <div className="qb-empty">No rules. Click + RULE to add one.</div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Pecuscan : The Pecu Novus Blockchain Explorer</title>
        <meta name="description" content="PECUSCAN" />
        <meta name="keywords" content="Pecu, Pecu Novus" />
      </Helmet>

      {/* Inline styles for the query builder so you don't have to edit your CSS file */}
      <style>{`
        .term-search-row2 {
          display: flex; align-items: center; gap: 12px;
          padding: 0 16px 10px; border-bottom: 1px solid var(--term-border, #1a2332);
          background: var(--term-bg-2, #0a0e1a);
          flex-wrap: wrap;
        }
        .term-adv-btn {
          background: transparent; color: var(--term-text, #e5e7eb);
          border: 1px solid var(--term-border, #1f2937);
          padding: 6px 12px; font-family: 'JetBrains Mono', monospace;
          font-size: 11px; letter-spacing: 1px; cursor: pointer;
          display: inline-flex; align-items: center; gap: 8px;
        }
        .term-adv-btn:hover { border-color: #f59e0b; color: #f59e0b; }
        .term-adv-btn.active { border-color: #f59e0b; color: #f59e0b; background: rgba(245,158,11,0.06); }
        .term-adv-badge {
          background: #f59e0b; color: #0a0e1a; font-size: 9px;
          padding: 1px 6px; border-radius: 2px; font-weight: 700;
        }
        .term-filter-status {
          font-size: 10px; color: #6b7280; letter-spacing: 1px;
          font-family: 'JetBrains Mono', monospace;
        }
        .term-filter-status .tick-up { color: #10b981; }

        /* Panel */
        .qb-panel {
          background: #0a0e1a; border: 1px solid #1a2332;
          border-top: 2px solid #f59e0b;
          margin: 0 16px 16px; padding: 16px;
          font-family: 'JetBrains Mono', monospace;
        }
        .qb-panel-head {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 14px; padding-bottom: 10px;
          border-bottom: 1px solid #1a2332;
        }
        .qb-panel-title {
          color: #f59e0b; font-size: 11px; letter-spacing: 2px; font-weight: 700;
        }
        .qb-scope { display: flex; gap: 4px; }
        .qb-scope button {
          background: transparent; color: #6b7280;
          border: 1px solid #1f2937; padding: 4px 10px;
          font-family: inherit; font-size: 10px; letter-spacing: 1px; cursor: pointer;
        }
        .qb-scope button.active { background: #1a2332; color: #e5e7eb; border-color: #f59e0b; }

        /* Group */
        .qb-group {
          border-left: 2px solid #1f2937; padding: 10px 0 10px 12px;
          margin-bottom: 10px;
        }
        .qb-group.depth-0 { border-left-color: #f59e0b; }
        .qb-group.depth-1 { border-left-color: #10b981; }
        .qb-group.depth-2 { border-left-color: #3b82f6; }
        .qb-group.depth-3 { border-left-color: #a855f7; }
        .qb-group-head {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 8px;
        }
        .qb-combinator { display: flex; gap: 4px; }
        .qb-pill {
          background: transparent; color: #6b7280;
          border: 1px solid #1f2937; padding: 3px 10px;
          font-family: inherit; font-size: 10px; font-weight: 700;
          letter-spacing: 1px; cursor: pointer;
        }
        .qb-pill.active { background: #f59e0b; color: #0a0e1a; border-color: #f59e0b; }
        .qb-pill-not.active { background: #ef4444; border-color: #ef4444; color: #fff; }
        .qb-group-actions { display: flex; gap: 4px; }

        /* Rule */
        .qb-rule {
          display: flex; gap: 6px; align-items: center;
          margin: 6px 0; flex-wrap: wrap;
        }
        .qb-select, .qb-input {
          background: #050810; color: #e5e7eb;
          border: 1px solid #1f2937; padding: 5px 8px;
          font-family: inherit; font-size: 11px;
        }
        .qb-select:focus, .qb-input:focus { outline: none; border-color: #f59e0b; }
        .qb-op { min-width: 70px; }
        .qb-input { min-width: 140px; }
        .qb-and { color: #6b7280; font-size: 10px; letter-spacing: 1px; }

        /* Buttons */
        .qb-btn {
          background: transparent; color: #e5e7eb;
          border: 1px solid #1f2937; padding: 4px 10px;
          font-family: inherit; font-size: 10px; letter-spacing: 1px; cursor: pointer;
        }
        .qb-btn:hover { border-color: #f59e0b; color: #f59e0b; }
        .qb-btn-danger { color: #ef4444; border-color: #2a1a1a; }
        .qb-btn-danger:hover { border-color: #ef4444; background: rgba(239,68,68,0.1); }
        .qb-btn-primary { background: #f59e0b; color: #0a0e1a; border-color: #f59e0b; font-weight: 700; }
        .qb-btn-primary:hover { background: #fbbf24; color: #0a0e1a; }

        .qb-empty { color: #4b5563; font-size: 10px; padding: 6px 0; font-style: italic; }

        /* Footer */
        .qb-footer {
          display: flex; justify-content: space-between; align-items: center;
          margin-top: 14px; padding-top: 12px;
          border-top: 1px solid #1a2332;
        }
        .qb-summary { color: #6b7280; font-size: 10px; letter-spacing: 1px; }
        .qb-summary b { color: #f59e0b; }
        .qb-actions { display: flex; gap: 8px; }
      `}</style>

      <div style={{ background: "var(--term-bg)", minHeight: "100vh" }}>
        {/* ===== TOP STATUS BAR ===== */}
        <div className="term-topbar">
          <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
            <span className="term-logo">PECUSCAN</span>
            <span style={{ color: "var(--term-text-muted)", fontSize: "11px", letterSpacing: "1px" }}>
              PECU NOVUS • MAINNET
            </span>
          </div>
          <div style={{ display: "flex", gap: "24px", alignItems: "center" }}>
            <Link
              to="/rpc"
              className="term-rpc-btn"
              title="JSON-RPC Explorer — every method the PecuNovus bridge answers"
            >
              ▸ RPC
            </Link>
            <span style={{ color: "var(--term-text-muted)", fontSize: "11px" }}>{clock}</span>
            <span className="term-status">
              <span className="term-dot"></span>LIVE
            </span>
          </div>
        </div>

        {/* ===== TICKER ===== */}
        <div className="term-ticker">
          <div className="tick">
            <span className="tick-sym">PECU/USD</span>
            <span className="tick-val">
              ${currentValue !== null ? Number(currentValue).toLocaleString("en-US") : "—"}
            </span>
            {renderTrend(trend24)}
          </div>
          <div className="tick"><span className="tick-sym">1H</span>{renderTrend(trendHourly)}</div>
          <div className="tick"><span className="tick-sym">MCAP</span><span className="tick-val">${fmt(marketCap)}</span></div>
          <div className="tick"><span className="tick-sym">VOL (PECU)</span><span className="tick-val">{fmt(coinVolume)}</span></div>
          <div className="tick">
            <span className="tick-sym">LATEST BLOCK</span>
            <span className="tick-val">{latestBlock ? latestBlock.toLocaleString() : "—"}</span>
          </div>
          <div className="tick"><span className="tick-sym">CIRC SUPPLY</span><span className="tick-val">{fmt(circulating)}</span></div>
        </div>

        {/* ===== SEARCH (row 1) ===== */}
        <div className="term-search">
          <span className="term-search-title">▸ QUERY</span>
          <select>
            <option>ALL FILTERS</option>
            <option>ADDRESSES</option>
            <option>TOKENS</option>
            <option>NAME TAGS</option>
            <option>LABELS</option>
          </select>
          <input
            type="text"
            autoComplete="off"
            placeholder="Search by address / txn hash / block / escrow pub key..."
            value={searchBlock}
            onChange={(e) => setSearchBlock(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <button type="button" onClick={runSearch} disabled={searching}>
            {searching ? "SEARCHING…" : "SEARCH"}
          </button>
          <button
            type="button"
            className={`term-adv-btn ${filterOpen ? "active" : "active"}`}
            onClick={() => setFilterOpen((v) => !v)}
          >
          <TuneOutlined style={{ fontSize: "16px" }} />
           
            {filterActive && <span className="term-adv-badge">{ruleCount}</span>}
            <span style={{ fontSize: "9px", opacity: 0.6 }}>{filterOpen ? "▲" : "▼"}</span>
          </button>
        </div>

        {/* ===== SEARCH NOTICE (no direct route / not found) ===== */}
        {searchNotice && (
          <div className="term-search-row2">
            <span className="term-filter-status">{searchNotice}</span>
          </div>
        )}

        {/* ===== SEARCH (row 2: advanced toggle + status) ===== */}
        <div className="term-search-row2">
          {/* <button
            type="button"
            className={`term-adv-btn ${filterOpen ? "active" : ""}`}
            onClick={() => setFilterOpen((v) => !v)}
          >
            <span>▸ ADVANCED FILTERS</span>
            {filterActive && <span className="term-adv-badge">{ruleCount}</span>}
            <span style={{ fontSize: "9px", opacity: 0.6 }}>{filterOpen ? "▲" : "▼"}</span>
          </button> */}

          {filterActive && (
            <span className="term-filter-status">
              FILTER ACTIVE • SHOWING <span className="tick-up">{filteredBlocks.length}</span> /{" "}
              {blocks.length} BLOCKS
            </span>
          )}

          {filterActive && (
            <button type="button" className="qb-btn qb-btn-danger" onClick={handleClear}>
              CLEAR FILTER
            </button>
          )}
        </div>

        {/* ===== ADVANCED FILTER PANEL ===== */}
        {filterOpen && (
          <div className="qb-panel">
            <div className="qb-panel-head">
              <span className="qb-panel-title">▸ QUERY BUILDER</span>
              <div className="qb-scope">
                {["ALL", "BLOCK", "TX"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={filterScope === s ? "active" : ""}
                    onClick={() => setFilterScope(s)}
                  >
                    {s} FIELDS
                  </button>
                ))}
              </div>
            </div>

            {renderGroup(queryTree, 0)}

            <div className="qb-footer">
              <div className="qb-summary">
                <b>{ruleCount}</b> RULE{ruleCount !== 1 ? "S" : ""} · WILL MATCH{" "}
                <b>{blocks.filter((b) => evalNode(queryTree, b)).length}</b> /{" "}
                {blocks.length} BLOCKS
              </div>
              <div className="qb-actions">
                <button type="button" className="qb-btn" onClick={handleClear}>RESET</button>
                <button type="button" className="qb-btn qb-btn-primary" onClick={handleApply}>
                  APPLY FILTER
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ===== HERO PRICE CARD ===== */}
        <div className="term-hero">
          <div className="term-hero-left">
            <div className="term-hero-lbl">CURRENT PRICE</div>
            <div className="term-hero-price">
              ${currentValue !== null ? Number(currentValue).toLocaleString("en-US") : "—"}
            </div>
          </div>
          <div className="term-hero-trends">
            <div className={`term-hero-trend ${trendHourly?.trend === 1 ? "up" : trendHourly ? "dn" : ""}`}>
              <div className="term-hero-trend-lbl">1 HOUR</div>
              <div className="term-hero-trend-val">
                {trendHourly ? `${trendHourly.trend === 1 ? "▲ +" : "▼ "}${trendHourly.value}%` : "—"}
              </div>
            </div>
            <div className={`term-hero-trend ${trend24?.trend === 1 ? "up" : trend24 ? "dn" : ""}`}>
              <div className="term-hero-trend-lbl">24 HOURS</div>
              <div className="term-hero-trend-val">
                {trend24 ? `${trend24.trend === 1 ? "▲ +" : "▼ "}${trend24.value}%` : "—"}
              </div>
            </div>
          </div>
        </div>

        {/* ===== STATS ===== */}
        <div className="term-stats">
          <div className="term-stat">
            <div className="term-stat-lbl">MARKET CAP</div>
            <div className="term-stat-val">${fmt(marketCap)}</div>
            <div className="term-stat-chg tick-up">USD</div>
          </div>
          <div className="term-stat">
            <div className="term-stat-lbl">VOLUME (PECU)</div>
            <div className="term-stat-val">{fmt(coinVolume)}</div>
            <div className="term-stat-chg tick-up">24h</div>
          </div>
          <div className="term-stat">
            <div className="term-stat-lbl">VOLUME (USD)</div>
            <div className="term-stat-val">${fmt(usdVolume)}</div>
            <div className="term-stat-chg tick-up">24h</div>
          </div>
          <div className="term-stat">
            <div className="term-stat-lbl">CIRCULATING</div>
            <div className="term-stat-val">{fmt(circulating)}</div>
            <div className="term-stat-chg tick-up">PECU</div>
          </div>
          <div className="term-stat">
            <div className="term-stat-lbl">TOTAL SUPPLY</div>
            <div className="term-stat-val">{fmt(totalSupply)}</div>
            <div className="term-stat-chg tick-up">PECU</div>
          </div>
          <div className="term-stat">
            <div className="term-stat-lbl">MAX SUPPLY</div>
            <div className="term-stat-val">{fmt(maxSupply)}</div>
            <div className="term-stat-chg tick-up">PECU</div>
          </div>
        </div>

        {/* ===== CHARTS (now use filteredBlocks) ===== */}
        <div className="term-grid">
          <div className="term-panel full">
            <div className="term-panel-head">
              <span className="term-panel-title">▸ BLOCK VALUE • RECENT BLOCKS</span>
              <span className="term-panel-sub">
                {filteredBlocks.length > 0
                  ? `LAST ${Math.min(filteredBlocks.length, 20)} BLOCKS${filterActive ? " (FILTERED)" : ""}`
                  : "LOADING..."}
              </span>
            </div>
            <div className="term-chart">
              <canvas ref={valChartRef} role="img" aria-label="Block value in USD over recent blocks" />
            </div>
          </div>
        </div>

        {/* ===== BLOCKS + LEDGER (gets filtered list) ===== */}
        <Block
          searchBlock={searchBlock}
          preloadedBlocks={filteredBlocks}
          loading={blocksLoading}
        />
      </div>
    </>
  );
}

export default SearchBar;